import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  motion, 
  useScroll, 
  useTransform, 
  useSpring, 
  useMotionValue,
  useInView 
} from 'framer-motion';
import { 
  FaChevronDown,
  FaRocket,
  FaBrain,
  FaHeart,
  FaLightbulb,
  FaPlay,
  FaArrowRight,
  FaCheck,
  FaShieldAlt,
  FaGlobe,
  FaUsers
} from 'react-icons/fa';
import useIsMobile from '../hooks/useIsMobile';

// Modern Hero Section with 3D Effects
const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 30%, #ffffff 70%, #f5f7ff 100%),
    radial-gradient(circle at 25% 75%, rgba(0, 122, 255, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 75% 25%, rgba(255, 59, 48, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(48, 209, 88, 0.02) 0%, transparent 50%);
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 1px 1px, rgba(0, 122, 255, 0.1) 1px, transparent 0);
    background-size: 40px 40px;
    opacity: 0.3;
    animation: dotMove 15s linear infinite, dotBreathe 6s ease-in-out infinite;
  }
  
  @keyframes dotMove {
    0% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(20px, -10px) scale(1.1); }
    66% { transform: translate(40px, 20px) scale(0.9); }
    100% { transform: translate(60px, 40px) scale(1); }
  }
  
  @keyframes dotBreathe {
    0%, 100% { opacity: 0.3; background-size: 40px 40px; }
    50% { opacity: 0.6; background-size: 50px 50px; }
  }
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.06) 0%, transparent 40%),
    radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.04) 0%, transparent 40%),
    radial-gradient(circle at 60% 60%, rgba(48, 209, 88, 0.03) 0%, transparent 40%);
`;

const FloatingShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const GlassShape = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: ${props => props.radius || '20px'};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const ColoredGlass = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.radius || '50%'};
  box-shadow: 
    0 6px 24px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 25%, 
    #5856D6 50%, 
    #FF3B30 75%, 
    #1a1a1a 100%);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientFlow 8s ease infinite;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @keyframes gradientFlow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(26, 26, 26, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${props => props.theme.spacing.xxl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: rgba(26, 26, 26, 0.6);
  cursor: pointer;
  
  svg {
    font-size: 1.5rem;
    animation: floatDown 2s ease-in-out infinite;
  }
  
  @keyframes floatDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(10px); }
  }
  
  &:hover {
    color: #007AFF;
  }
`;

// Story Section with Morphing Shapes
const StorySection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const MorphingShape = styled(motion.div)`
  position: absolute;
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, 
    rgba(0, 122, 255, 0.1), 
    rgba(88, 86, 214, 0.05));
  border-radius: 50%;
  filter: blur(40px);
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxxl};
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xxl};
  }
`;

const StoryContent = styled(motion.div)`
  
`;

const StoryTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  line-height: 1.1;
`;

const StoryText = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const VideoContainer = styled(motion.div)`
  position: relative;
  border-radius: ${props => props.theme.radius.xlarge};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.xlarge};
  background: linear-gradient(135deg, 
    rgba(0, 122, 255, 0.1), 
    rgba(88, 86, 214, 0.05));
  aspect-ratio: 16/9;
  cursor: pointer;
`;

const VideoOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
`;

const PlayButton = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-left: 4px;
  }
`;

// Values Section with 3D Card Effects
const ValuesSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.backgroundAlt};
  position: relative;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xxxl};
`;

const ValueCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 122, 255, 0.05), rgba(88, 86, 214, 0.02));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const ValueIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  font-size: 2rem;
  color: white;
  position: relative;
  z-index: 2;
  
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
    border-radius: 50%;
    z-index: -1;
    filter: blur(20px);
    opacity: 0.3;
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  position: relative;
  z-index: 2;
`;

const ValueDescription = styled.p`
  color: rgba(26, 26, 26, 0.7);
  line-height: 1.6;
  position: relative;
  z-index: 2;
`;

// Stats Section with Animated Counters
const StatsSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.xl};
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const StatNumber = styled(motion.div)`
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #007AFF, #5856D6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatLabel = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;
`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

// Animated Counter Hook
const useCounter = (end, start = 0, duration = 2000) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, start, duration]);
  
  return [count, setIsVisible];
};

// Magnetic Element Component
const MagneticElement = ({ children, strength = 0.3, ...props }) => {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    // Disable magnetic effect on mobile devices
    if (isMobile || !ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * strength);
    y.set((e.clientY - centerY) * strength);
  };

  const handleMouseLeave = () => {
    // Disable magnetic effect on mobile devices
    if (isMobile) return;
    
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={isMobile ? {} : { x: springX, y: springY }}
      onMouseMove={isMobile ? undefined : handleMouseMove}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const StatCardComponent = ({ stat, index, setCursorVariant }) => {
  const [count, setIsVisible] = useCounter(stat.number);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView, setIsVisible]);

  return (
    <StatCard
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2, duration: 0.8 }}
      whileHover={{ scale: 1.05 }}
      onMouseEnter={() => setCursorVariant("text")}
      onMouseLeave={() => setCursorVariant("default")}
    >
      <StatNumber>
        {count}{stat.suffix}
      </StatNumber>
      <StatLabel>{stat.label}</StatLabel>
    </StatCard>
  );
};

const About = ({ setCursorVariant }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.8]);

  const [particles, setParticles] = useState([]);

  useEffect(() => {
    document.title = "Chi Siamo | D-Soft";
    window.scrollTo(0, 0);

    // Generate particles
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      color: `rgba(${Math.random() > 0.5 ? '0, 122, 255' : '255, 255, 255'}, ${Math.random() * 0.6 + 0.1})`,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 2
    }));
    setParticles(newParticles);
  }, []);

  const values = [
    {
      icon: FaRocket,
      title: "Innovazione",
      description: "Spingiamo i confini e abbracciamo tecnologie all'avanguardia per creare soluzioni rivoluzionarie.",
      color1: "#007AFF",
      color2: "#5856D6"
    },
    {
      icon: FaBrain,
      title: "Intelligenza",
      description: "Decisioni basate sui dati e algoritmi intelligenti alimentano tutto ciò che costruiamo.",
      color1: "#FF3B30",
      color2: "#FF9500"
    },
    {
      icon: FaHeart,
      title: "Passione",
      description: "Amiamo quello che facciamo e si vede in ogni riga di codice e pixel che creiamo.",
      color1: "#30D158",
      color2: "#32D74B"
    },
    {
      icon: FaLightbulb,
      title: "Creatività",
      description: "Soluzioni uniche richiedono pensiero creativo e approcci innovativi.",
      color1: "#FFD60A",
      color2: "#FFCC02"
    }
  ];

  const stats = [
    { number: 150, label: "Progetti Completati", suffix: "+" },
    { number: 50, label: "Clienti Soddisfatti", suffix: "+" },
    { number: 5, label: "Anni di Esperienza", suffix: "" },
    { number: 99, label: "Tasso di Successo", suffix: "%" }
  ];

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: parallaxY, opacity, scale }} />
        
        <FloatingShapes>
          {/* Main Glass Morphism Elements */}
          <GlassShape
            radius="60px"
            animate={{
              x: [0, 150, -100, 0],
              y: [0, -100, 50, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.1, 0.9, 1],
            }}
            transition={{
              duration: 28,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '250px',
              height: '250px',
              top: '15%',
              left: '8%',
            }}
          />
          
          <GlassShape
            radius="50%"
            animate={{
              x: [0, -180, 120, 0],
              y: [0, 80, -90, 0],
              rotate: [0, -120, -240, -360],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              width: '180px',
              height: '180px',
              top: '60%',
              right: '12%',
            }}
          />

          {/* Colored Glass Elements */}
          {[...Array(5)].map((_, i) => (
            <ColoredGlass
              key={i}
              gradient={`linear-gradient(135deg, 
                rgba(${['0, 122, 255', '255, 59, 48', '48, 209, 88', '255, 204, 0', '88, 86, 214'][i]}, 0.15) 0%, 
                rgba(${['88, 86, 214', '255, 149, 0', '50, 215, 75', '255, 214, 10', '0, 122, 255'][i]}, 0.08) 100%)`}
              radius={i % 2 === 0 ? '50%' : '25px'}
              animate={{
                x: [0, 60 + i * 20, -30 - i * 10, 0],
                y: [0, -40 - i * 15, 60 + i * 10, 0],
                rotate: [0, 90 + i * 30, 180 + i * 60, 360],
                scale: [1, 1.15, 0.85, 1],
              }}
              transition={{
                duration: 22 + i * 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                width: `${80 + i * 25}px`,
                height: `${80 + i * 25}px`,
                top: `${12 + i * 18}%`,
                left: `${8 + i * 18}%`,
              }}
            />
          ))}

          {/* Geometric Pattern Overlay */}
          <motion.div
            style={{
              position: 'absolute',
              width: '400px',
              height: '400px',
              top: '25%',
              right: '5%',
              background: `
                linear-gradient(45deg, rgba(0, 122, 255, 0.04) 25%, transparent 25%), 
                linear-gradient(-45deg, rgba(0, 122, 255, 0.04) 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, rgba(255, 59, 48, 0.02) 75%), 
                linear-gradient(-45deg, transparent 75%, rgba(255, 59, 48, 0.02) 75%)
              `,
              backgroundSize: '30px 30px',
              borderRadius: '50%',
              opacity: 0.6,
            }}
            animate={{
              rotate: [0, 360],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 50,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          {/* Floating Dots */}
          <motion.div
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              top: '5%',
              right: '25%',
              background: `radial-gradient(circle at 2px 2px, rgba(48, 209, 88, 0.3) 2px, transparent 0)`,
              backgroundSize: '25px 25px',
              borderRadius: '50%',
              opacity: 0.4,
            }}
            animate={{
              x: [0, 50, -30, 0],
              y: [0, -30, 40, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </FloatingShapes>

        <HeroContent>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <HeroTitle 
              variants={fadeInUp}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Chi Siamo
            </HeroTitle>
            
            <HeroSubtitle variants={fadeInUp}>
              Creiamo soluzioni digitali innovative che trasformano idee in realtà. 
              Con passione e competenza, aiutiamo le aziende a crescere nell'era digitale.
            </HeroSubtitle>
          </motion.div>
        </HeroContent>

        <ScrollIndicator
          onClick={scrollToContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          onMouseEnter={() => setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant("default")}
        >
          <span>Scopri la Nostra Storia</span>
          <FaChevronDown />
        </ScrollIndicator>
      </HeroSection>

      {/* Story Section */}
      <StorySection>
        <MorphingShape
          style={{
            top: "10%",
            left: "10%",
          }}
          animate={{
            borderRadius: ["50%", "30% 70%", "70% 30%", "50%"],
            rotate: [0, 90, 180, 270, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        
        <MorphingShape
          style={{
            bottom: "10%",
            right: "10%",
          }}
          animate={{
            borderRadius: ["50%", "70% 30%", "30% 70%", "50%"],
            rotate: [360, 270, 180, 90, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        <div className="container">
          <StoryContent
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}
          >
            <StoryTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              La Nostra Storia
            </StoryTitle>
            
            <StoryText>
              Fondata nel 2019, D-Soft è nata da una semplice convinzione: 
              la tecnologia dovrebbe potenziare il potenziale umano, non complicarlo. 
              Quello che è iniziato come un piccolo team di sviluppatori appassionati è cresciuto 
              fino a diventare un'azienda dinamica che serve clienti in tutto il mondo.
            </StoryText>
            
            <StoryText>
              Siamo sempre stati guidati dalla curiosità e dal desiderio di risolvere 
              problemi complessi con soluzioni eleganti. Ogni progetto che intraprendiamo 
              è un'opportunità per spingere i confini e creare qualcosa 
              di straordinario.
            </StoryText>
          </StoryContent>
        </div>
      </StorySection>

      {/* Values Section */}
      <ValuesSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center' }}
          >
            <h2 
              style={{ fontSize: '3rem', marginBottom: '1rem' }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              I Nostri Valori
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#86868B', maxWidth: '600px', margin: '0 auto' }}>
              I principi che guidano ogni decisione che prendiamo e ogni soluzione che creiamo
            </p>
          </motion.div>

          <ValuesGrid>
            {values.map((value, index) => (
              <MagneticElement key={index}>
                <ValueCard
                  initial={{ opacity: 0, y: 50, rotateX: -15 }}
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                  whileHover={{ 
                    rotateY: 10, 
                    scale: 1.05,
                    transition: { duration: 0.3 }
                  }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  onMouseEnter={() => setCursorVariant("link")}
                  onMouseLeave={() => setCursorVariant("default")}
                >
                  <ValueIcon
                    color1={value.color1}
                    color2={value.color2}
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: 5,
                      boxShadow: "0 20px 40px rgba(0, 122, 255, 0.3)"
                    }}
                  >
                    <value.icon />
                  </ValueIcon>
                  
                  <ValueTitle>{value.title}</ValueTitle>
                  <ValueDescription>{value.description}</ValueDescription>
                </ValueCard>
              </MagneticElement>
            ))}
          </ValuesGrid>
        </div>
      </ValuesSection>

      {/* Stats Section */}
      <StatsSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 
              style={{ fontSize: '3rem', marginBottom: '1rem' }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Il Nostro Impatto
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#86868B' }}>
              Numeri che raccontano la nostra storia
            </p>
          </motion.div>

          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCardComponent key={index} stat={stat} index={index} setCursorVariant={setCursorVariant} />
            ))}
          </StatsGrid>
        </div>
      </StatsSection>
    </div>
  );
};

export default About; 